import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ChevronDown,
  ChevronRight,
  Download,
  Eye,
  Filter,
  FileText,
  RefreshCw,
  Star,
  Trash2,
} from 'lucide-react';
import { format } from 'date-fns';
import toast from 'react-hot-toast';

import PageHeader from '../../components/layout/PageHeader';
import DocumentPreviewModal, {
  type PreviewContentKind,
} from '../../components/ui/DocumentPreviewModal';
import Table, { type TableColumn } from '../../components/ui/Table';
import SearchableSelect from '../../components/ui/SearchableSelect';
import HierarchyNodePicker from '../../components/hierarchy/HierarchyNodePicker';
import Button from '../../components/ui/Button';
import { useAuthStore } from '../../store/authStore';
import documentService from '../../services/document.service';
import approvalService from '../../services/approval.service';
import hierarchyService from '../../services/hierarchy.service';
import { searchService } from '../../services/search.service';
import { ROUTES } from '../../config/routes.config';
// import { formatDocumentDisplayName } from '../../utils/documentName';
import type { CaseSearchParams } from '../../types/search.types';
import {
  canDownloadDocument,
  canPreviewDocument,
} from '../../utils/documentAccessMode';
import { getFilterYearSelectOptions } from '../../utils/filterYearOptions';

type TabKey = 'Pending' | 'Approved' | 'Denied';

interface UploadStatusRow {
  document_id: string;
  original_file_name: string;
  file_extension?: string;
  case_id: string;
  cms_number: string;
  fir_number?: string;
  ps_name?: string;
  cms_case_name?: string;
  rrms_ref_number: string;
  folder_type: string;
  document_type: string;
  classification: string;
  approval_status: string;
  created_at?: string;
  uploaded_at?: string;
  denial_reason?: string;
  decision_at?: string;
  decided_by_user_id?: number;
  action_by?: string;
  is_favorite?: boolean;
  uploaded_by_user_id?: number;
}

const PAGE_SIZE = 10;
const FILE_EXTENSIONS = ['', 'PDF', 'DOCX', 'XLSX', 'JPG', 'PNG', 'MP4', 'MP3'];
const CLASSIFICATION_OPTS: { id: string; name: string }[] = [
  { id: 'Public', name: 'Public' },
  { id: 'Confidential', name: 'Confidential' },
];
const DEFAULT_FILTERS: CaseSearchParams = {};

const stripLeadingCasePrefixFromFileName = (fileName: string): string => {
  const name = String(fileName || '').trim();
  if (!name) return '';
  // Legacy uploads may store names like "<cms_or_fir>__actual-file.pdf".
  const cleaned = name.replace(/^\d{4,}(?:[/-]\d{1,4})?[_\-\s]+/, '');
  return cleaned || name;
};

const normalizeSelectOptions = (input: unknown): { id: string; name: string }[] => {
  if (!Array.isArray(input)) return [];
  return input
    .map((item) => {
      if (typeof item === 'string') return { id: item, name: item };
      if (item && typeof item === 'object') {
        const obj = item as Record<string, unknown>;
        const rawId = obj.id ?? obj.value ?? obj.code ?? obj.label ?? obj.name;
        const rawName = obj.name ?? obj.label ?? obj.value ?? obj.id;
        if (rawId != null && rawName != null) {
          return { id: String(rawId), name: String(rawName) };
        }
      }
      return null;
    })
    .filter((opt): opt is { id: string; name: string } => !!opt);
};

const UploadStatusPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { role, user, can } = useAuthStore();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<TabKey>('Pending');
  const [page, setPage] = useState(1);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [actingId, setActingId] = useState<string | null>(null);
  const [rejectModal, setRejectModal] = useState<{ documentId: string; fileName: string } | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [previewDocId, setPreviewDocId] = useState<string | null>(null);
  const [previewContentKind, setPreviewContentKind] =
    useState<PreviewContentKind | null>(null);
  const [accessDeniedDialogDocId, setAccessDeniedDialogDocId] = useState<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<{ documentId: string; fileName: string } | null>(null);
  const [filters, setFilters] = useState<CaseSearchParams>(DEFAULT_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState<CaseSearchParams>(DEFAULT_FILTERS);
  // Bumped on Clear to force the cascading Section/Sub-Section picker to reset.
  const [filterResetKey, setFilterResetKey] = useState(0);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [hashtagSuggestions, setHashtagSuggestions] = useState<string[]>([]);

  const setFilter = useCallback(
    (key: keyof CaseSearchParams, value: string | number | undefined) => {
      setFilters((prev) => ({
        ...prev,
        [key]: value === '' || value === undefined ? undefined : value,
      }));
    },
    []
  );

  useEffect(() => {
    document.title = 'RRMS | Upload Status';
  }, []);

  useEffect(() => {
    const tab = (searchParams.get('tab') || '').toLowerCase();
    if (tab === 'pending') {
      setActiveTab('Pending');
      setPage(1);
    }
    if (tab === 'approved') {
      setActiveTab('Approved');
      setPage(1);
    }
    if (tab === 'denied' || tab === 'rejected') {
      setActiveTab('Denied');
      setPage(1);
    }
  }, [searchParams]);

  const statusParam =
    activeTab === 'Denied' ? 'Denied' : activeTab;
  const effectiveStatus = statusParam;

  // File Category master (admin-created lookup template) — same source the
  // Upload form uses, so filter options track the admin master automatically.
  const lookupTemplatesQuery = useQuery({
    queryKey: ['upload-status-lookup-templates'],
    queryFn: () => hierarchyService.getLookupTemplates(),
  });
  const lookupTemplates: { id: number; template_key: string; name: string }[] =
    lookupTemplatesQuery.data?.data?.data ?? [];
  const categoryTemplate = lookupTemplates.find(
    (t) => /categor/i.test(t.name) || /categor/i.test(t.template_key)
  );
  const categoryNodesQuery = useQuery({
    queryKey: ['upload-status-category-nodes', categoryTemplate?.template_key],
    queryFn: () => hierarchyService.getTemplateNodes(categoryTemplate!.template_key),
    enabled: !!categoryTemplate,
  });
  const categoryOptions: { id: string; name: string }[] = (
    categoryNodesQuery.data?.data?.data ?? []
  ).map((n: any) => ({ id: String(n.id), name: n.label || `#${n.id}` }));
  const categoryLabel = categoryTemplate?.name ?? 'File Category';

  const folderTypesQuery = useQuery({
    queryKey: ['upload-status-filter-folder-types'],
    queryFn: () => searchService.getFolderTypes(),
  });
  const documentTypesQuery = useQuery({
    queryKey: ['upload-status-filter-document-types', filters.folder_type],
    queryFn: () => searchService.getDocumentTypes(filters.folder_type),
    enabled: true,
  });

  const query = useQuery({
    queryKey: ['upload-status', activeTab, page, appliedFilters],
    queryFn: () =>
      documentService.getUploadStatus({
        status: effectiveStatus,
        fir_number: appliedFilters.fir_number,
        node_id: appliedFilters.node_id,
        category_node_id: appliedFilters.category_node_id,
        subject: appliedFilters.subject,
        year: appliedFilters.year,
        classification: appliedFilters.classification,
        folder_type: appliedFilters.folder_type,
        document_type: appliedFilters.document_type,
        file_extension: appliedFilters.file_extension,
        hashtags: appliedFilters.hashtags,
        page,
        limit: PAGE_SIZE,
      }),
  });

  const folderTypeOpts = useMemo(
    () => normalizeSelectOptions(folderTypesQuery.data),
    [folderTypesQuery.data]
  );
  const docTypeOpts = useMemo(
    () => normalizeSelectOptions(documentTypesQuery.data),
    [documentTypesQuery.data]
  );
  const yearOpts = useMemo(() => getFilterYearSelectOptions(), []);
  const extOpts = useMemo(
    () => FILE_EXTENSIONS.map((e) => ({ id: e, name: e })),
    []
  );

  const handleApplyFilters = () => {
    setAppliedFilters(filters);
    setPage(1);
    setIsFilterModalOpen(false);
  };

  const handleClearFilters = () => {
    setFilters(DEFAULT_FILTERS);
    setAppliedFilters(DEFAULT_FILTERS);
    setHashtagSuggestions([]);
    setFilterResetKey((k) => k + 1);
    setPage(1);
    setIsFilterModalOpen(false);
  };

  const rawData = query.data?.data?.data ?? query.data?.data;
  const items: UploadStatusRow[] = Array.isArray(rawData?.items)
    ? rawData.items
    : [];
  const pagination =
    rawData?.pagination ?? {
      total: items.length,
      page,
      limit: PAGE_SIZE,
      totalPages: 1,
      hasNextPage: false,
      hasPrevPage: false,
    };

  useEffect(() => {
    if (query.error) {
      const msg =
        (query.error as any)?.response?.data?.message ??
        'Failed to load upload status.';
      toast.error(msg);
    }
  }, [query.error]);

  const canReviewDocuments = can('records.approve', [
    'RRMS_CONTENT_MANAGER',
    'RRMS_SUPER_ADMIN',
  ]);

  const reviewMutation = useMutation({
    mutationFn: async (payload: {
      documentId: string;
      action: 'Approved' | 'Rejected';
      reason?: string;
    }) => {
      const reason = payload.action === 'Rejected' ? payload.reason || '' : '';
      return approvalService.decideDocument(
        payload.documentId,
        payload.action,
        reason
      );
    },
    onSuccess: (_resp, vars) => {
      toast.success(
        vars.action === 'Approved'
          ? 'Document approved successfully.'
          : 'Document rejected successfully.'
      );
      query.refetch();
    },
    onError: (err: any) => {
      const msg =
        err?.response?.data?.message ?? 'Failed to review document.';
      toast.error(msg);
    },
    onSettled: () => {
      setActingId(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (documentId: string) => documentService.deleteRejectedDocument(documentId),
    onSuccess: () => {
      toast.success('Rejected document removed from your list.');
      setDeleteConfirm(null);
      queryClient.invalidateQueries({ queryKey: ['upload-status'] });
    },
    onError: (err: any) => {
      const msg =
        err?.response?.data?.message ?? 'Failed to remove document.';
      toast.error(msg);
    },
    onSettled: () => {
      setActingId(null);
    },
  });

  const displayStatus = (status: string) =>
    status === 'Rejected' ? 'Denied' : status;

  const goToAccessRequest = (documentId: string) => {
    navigate(`${ROUTES.ACCESS_REQUEST}?document_id=${documentId}`);
  };

  const showAccessDeniedToast = (documentId: string) => {
    setAccessDeniedDialogDocId(documentId);
  };

  const openDownload = (documentId: string) => {
    documentService.downloadDocument(documentId).catch((err: any) => {
      const message = err?.message || 'Download failed';
      const accessDenied =
        err?.status === 403 ||
        /permission|access/i.test(String(message || ''));
      if (accessDenied) {
        showAccessDeniedToast(documentId);
        return;
      }
      toast.error(message);
    });
  };

  const openPreview = (documentId: string) => {
    setPreviewDocId(documentId);
  };

  const handleApprove = (documentId: string) => {
    setActingId(documentId);
    reviewMutation.mutate({ documentId, action: 'Approved' });
  };

  const openRejectModal = (documentId: string, fileName: string) => {
    setRejectModal({ documentId, fileName });
    setRejectReason('');
  };

  const handleRejectConfirm = async () => {
    if (!rejectReason.trim()) {
      toast.error('Please provide a rejection reason');
      return;
    }
    setActingId(rejectModal!.documentId);
    reviewMutation.mutate({
      documentId: rejectModal!.documentId,
      action: 'Rejected',
      reason: rejectReason.trim(),
    });
    setRejectModal(null);
  };

  const updateCachedFavorite = (documentId: string, isFavorite: boolean) => {
    queryClient.setQueriesData(
      { queryKey: ['upload-status'] },
      (old: any) => {
        const data = old?.data?.data ?? old?.data;
        const items = Array.isArray(data?.items) ? data.items : [];
        if (!items.length) return old;
        const nextItems = items.map((row: UploadStatusRow) =>
          row.document_id === documentId
            ? { ...row, is_favorite: isFavorite }
            : row
        );
        const nextData = { ...data, items: nextItems };
        return old?.data?.data !== undefined
          ? { ...old, data: { ...old.data, data: nextData } }
          : { ...old, data: nextData };
      }
    );
  };

  const handleFavoriteToggle = async (
    documentId: string,
    isFavorite?: boolean
  ) => {
    const nextFavorite = !isFavorite;
    updateCachedFavorite(documentId, nextFavorite);
    try {
      if (isFavorite) {
        await documentService.removeFavorite(documentId);
        toast.success('Removed from favorites');
      } else {
        await documentService.addFavorite(documentId);
        toast.success('Added to favorites');
      }
      // Don't invalidate on success: refetch would overwrite cache and can race with
      // the DB, so the icon would flicker back. Optimistic update is the source of truth.
      // Next manual refetch or tab switch will load fresh data with is_favorite from API.
    } catch {
      toast.error('Failed to update favorites');
      updateCachedFavorite(documentId, !!isFavorite);
    }
  };

  const tabs: { key: TabKey; label: string }[] = [
    { key: 'Pending', label: 'Pending' },
    { key: 'Approved', label: 'Approved' },
    { key: 'Denied', label: 'Denied' },
  ];

  const columns: TableColumn<UploadStatusRow>[] = [
    ...(activeTab === 'Denied'
      ? [{
          key: 'expand',
          header: '',
          width: 48,
          tooltip: (row: UploadStatusRow) =>
            expandedId === row.document_id ? 'Collapse details' : 'Expand details',
          cellStyle: { padding: '10px 8px' },
          render: (row: UploadStatusRow) => {
            const isExpanded = expandedId === row.document_id;

            return (
              <button
                type="button"
                onClick={() =>
                  setExpandedId((id) => (id === row.document_id ? null : row.document_id))
                }
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, borderRadius: 'var(--radius-sm)' }}
                aria-label={isExpanded ? 'Collapse' : 'Expand'}
              >
                {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
              </button>
            );
          },
        } satisfies TableColumn<UploadStatusRow>]
      : []),
    {
      key: 'file_name',
      header: 'File Name',
      minWidth: 230,
      clampLines: 2,
      // tooltip: (row) =>
      //   formatDocumentDisplayName({
      //     cmsNumber: row.cms_number,
      //     originalFileName:
      //       row.file_extension && !row.original_file_name?.endsWith(row.file_extension)
      //         ? `${row.original_file_name}${row.file_extension}`
      //         : row.original_file_name,
      //     documentType: row.document_type,
      //   }),
      tooltip: (row) => {
        const rawName =
          row.file_extension && !row.original_file_name?.endsWith(row.file_extension)
            ? `${row.original_file_name}${row.file_extension}`
            : row.original_file_name;
        return stripLeadingCasePrefixFromFileName(rawName);
      },
      cellStyle: { fontWeight: 600, color: 'var(--color-primary)' },
      render: (row) => {
        const previewAllowedByType = canPreviewDocument({
          fileName: row.original_file_name,
          fileExtension: row.file_extension,
        });
        const rawLabel =
          row.file_extension && !row.original_file_name?.endsWith(row.file_extension)
            ? `${row.original_file_name}${row.file_extension}`
            : row.original_file_name;
        const label = stripLeadingCasePrefixFromFileName(rawLabel);

        return previewAllowedByType ? (
          <button
            type="button"
            onClick={() => openPreview(row.document_id)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600, color: 'var(--color-primary)', fontSize: 'inherit', textAlign: 'left', padding: 0 }}
          >
            {label}
          </button>
        ) : (
          <span>{label}</span>
        );
      },
    },
    {
      key: 'fir_number',
      header: 'File No',
      minWidth: 130,
      clampLines: 2,
      tooltip: (row) => row.fir_number ?? (row as any).cms_number ?? '—',
      render: (row) => (
        <span style={{ fontFamily: 'var(--font-family-mono)', fontSize: 'var(--font-size-sm)' }}>
          {row.fir_number ?? (row as any).cms_number ?? '—'}
        </span>
      ),
    },
    {
      key: 'year',
      header: 'Year',
      minWidth: 80,
      tooltip: (row) => String((row as any).year ?? ''),
      render: (row) => (row as any).year ?? '—',
    },
    {
      key: 'hierarchy_path',
      header: 'Section',
      minWidth: 190,
      clampLines: 2,
      tooltip: (row) => (row as any).hierarchy_path ?? '—',
      render: (row) => (row as any).hierarchy_path ?? '—',
    },
    {
      key: 'category',
      header: 'Category',
      minWidth: 120,
      clampLines: 2,
      tooltip: (row) => (row as any).category ?? '—',
      render: (row) => (row as any).category ?? '—',
    },
    {
      key: 'subject',
      header: 'Subject',
      minWidth: 190,
      clampLines: 2,
      tooltip: (row) => (row as any).subject ?? '—',
      render: (row) => (row as any).subject ?? '—',
    },
    {
      key: 'upload_date',
      header: 'Upload Date',
      minWidth: 170,
      tooltip: (row) => {
        const uploadedAt = row.uploaded_at ?? row.created_at ?? '';
        return uploadedAt ? format(new Date(uploadedAt), 'dd MMM yyyy, HH:mm') : '—';
      },
      render: (row) => {
        const uploadedAt = row.uploaded_at ?? row.created_at ?? '';
        return uploadedAt ? format(new Date(uploadedAt), 'dd MMM yyyy, HH:mm') : '—';
      },
    },
    {
      key: 'status',
      header: 'Status',
      minWidth: 120,
      tooltip: (row) => displayStatus(row.approval_status),
      render: (row) => (
        <span style={{
          display: 'inline-block',
          padding: '2px 10px',
          borderRadius: 'var(--radius-full)',
          fontSize: 'var(--font-size-xs)',
          fontWeight: 600,
          background:
            displayStatus(row.approval_status) === 'Pending'
              ? 'var(--color-warning-light)'
              : displayStatus(row.approval_status) === 'Approved'
              ? 'var(--color-success-light)'
              : 'var(--color-error-light)',
          color:
            displayStatus(row.approval_status) === 'Pending'
              ? 'var(--color-warning)'
              : displayStatus(row.approval_status) === 'Approved'
              ? 'var(--color-success)'
              : 'var(--color-error)',
        }}>
          {displayStatus(row.approval_status)}
        </span>
      ),
    },
    ...(activeTab === 'Denied'
      ? [{
          key: 'reason',
          header: 'Reason',
          minWidth: 220,
          clampLines: 2,
          tooltip: (row: UploadStatusRow) => row.denial_reason || '—',
          cellStyle: { color: 'var(--color-error)', fontSize: 'var(--font-size-xs)' },
          render: (row: UploadStatusRow) => row.denial_reason || '—',
        } satisfies TableColumn<UploadStatusRow>]
      : []),
    {
      key: 'actions',
      header: 'Actions',
      minWidth: activeTab === 'Denied' ? 300 : 240,
      tooltip: () => 'Document actions',
      render: (row) => {
        const downloadAllowedByType = canDownloadDocument({
          fileName: row.original_file_name,
          fileExtension: row.file_extension,
        });
        const previewAllowedByType = canPreviewDocument({
          fileName: row.original_file_name,
          fileExtension: row.file_extension,
        });
        const isRejected =
          row.approval_status === 'Rejected' || row.approval_status === 'Denied';
        const isUploader =
          user?.user_id != null &&
          row.uploaded_by_user_id != null &&
          row.uploaded_by_user_id === user.user_id;
        const showRemoveRejected =
          activeTab === 'Denied' && isRejected && isUploader;
        const hideDownloadWhilePdfOrImagePreview =
          previewDocId != null &&
          (previewContentKind === 'pdf' || previewContentKind === 'image') &&
          row.document_id === previewDocId;

        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            {previewAllowedByType && !downloadAllowedByType && (
              <button
                type="button"
                onClick={() => openPreview(row.document_id)}
                title="Preview"
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, color: 'var(--color-primary)', display: 'inline-flex', alignItems: 'center' }}
              >
                <Eye size={16} />
              </button>
            )}
            {downloadAllowedByType && !hideDownloadWhilePdfOrImagePreview && (
              <button
                type="button"
                onClick={() => openDownload(row.document_id)}
                title="Download"
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, color: 'var(--color-primary)', display: 'inline-flex', alignItems: 'center' }}
              >
                <Download size={16} />
              </button>
            )}
            <button
              type="button"
              onClick={() => handleFavoriteToggle(row.document_id, row.is_favorite)}
              title={row.is_favorite ? 'Remove from favorites' : 'Add to favorites'}
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, display: 'inline-flex', alignItems: 'center' }}
            >
              <Star
                size={16}
                fill={row.is_favorite ? 'var(--color-accent)' : 'none'}
                color={row.is_favorite ? 'var(--color-accent)' : 'var(--color-gray-400)'}
              />
            </button>
            {canReviewDocuments && activeTab === 'Pending' && (
              <>
                <button
                  type="button"
                  onClick={() => handleApprove(row.document_id)}
                  disabled={actingId === row.document_id}
                  style={{ display: 'inline-flex', alignItems: 'center', padding: '4px 10px', borderRadius: 'var(--radius-md)', border: 'none', background: 'var(--color-success)', color: 'white', fontSize: 'var(--font-size-xs)', fontWeight: 500, cursor: 'pointer', opacity: actingId === row.document_id ? 0.5 : 1 }}
                >
                  Approve
                </button>
                <button
                  type="button"
                  onClick={() => openRejectModal(row.document_id, row.original_file_name)}
                  disabled={actingId === row.document_id}
                  style={{ display: 'inline-flex', alignItems: 'center', padding: '4px 10px', borderRadius: 'var(--radius-md)', border: 'none', background: 'var(--color-error)', color: 'white', fontSize: 'var(--font-size-xs)', fontWeight: 500, cursor: 'pointer', opacity: actingId === row.document_id ? 0.5 : 1 }}
                >
                  Reject
                </button>
              </>
            )}
            {showRemoveRejected && (
              <button
                type="button"
                onClick={() =>
                  setDeleteConfirm({
                    documentId: row.document_id,
                    fileName: row.original_file_name,
                  })
                }
                disabled={actingId === row.document_id || deleteMutation.isPending}
                title="Remove from your list"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  padding: '4px 10px',
                  borderRadius: 'var(--radius-md)',
                  border: '1px solid var(--color-error)',
                  background: 'white',
                  color: 'var(--color-error)',
                  fontSize: 'var(--font-size-xs)',
                  fontWeight: 600,
                  cursor: 'pointer',
                  opacity: actingId === row.document_id || deleteMutation.isPending ? 0.5 : 1,
                  gap: 4,
                }}
              >
                <Trash2 size={14} aria-hidden />
                Remove
              </button>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <div className="">
      <PageHeader
        title="Upload Status"
        subtitle="View status of your uploaded documents"
        actions={
          <div className="inline-flex items-center gap-2">
            {activeTab === 'Pending' && (
              <button
                type="button"
                onClick={() => {
                  setFilters(appliedFilters);
                  setIsFilterModalOpen(true);
                }}
                className="inline-flex items-center gap-2 rounded-md border border-[var(--color-primary)] bg-[var(--color-primary)] px-3 py-2 text-md font-medium text-white hover:bg-[var(--color-primary-light)]"
              >
                <Filter size={14} />
                Filter
              </button>
            )}
            <button
              type="button"
              onClick={() => query.refetch()}
              disabled={query.isFetching}
              className="inline-flex items-center gap-2 rounded-md border border-gray-200 bg-white px-3 py-2 text-md font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              <RefreshCw
                size={14}
                className={query.isFetching ? 'animate-spin' : ''}
              />
              Refresh
            </button>
          </div>
        }
      />

      <div
        className="flex gap-1 border-b border-gray-200 mb-5"
        style={{
          display: 'flex',
          gap: 'var(--spacing-1)',
          borderBottom: '1px solid var(--color-gray-200)',
          marginBottom: 'var(--spacing-5)',
        }}
      >
        {tabs.map(({ key, label }) => (
          <button
            key={key}
            type="button"
            onClick={() => {
              setActiveTab(key);
              setPage(1);
              setExpandedId(null);
            }}
            className="px-4 py-2.5 text-md font-medium rounded-t-md transition-colors"
            style={{
              background:
                activeTab === key
                  ? 'var(--color-primary-50)'
                  : 'transparent',
              color:
                activeTab === key
                  ? 'var(--color-primary)'
                  : 'var(--color-gray-600)',
              borderBottom:
                activeTab === key
                  ? '2px solid var(--color-primary)'
                  : '2px solid transparent',
              marginBottom: -1,
              cursor: 'pointer',
            }}
          >
            {label}
          </button>
        ))}
      </div>

      {isFilterModalOpen && (
        <div className="fixed inset-0 z-[1200] bg-black/45 p-4 sm:p-6">
          <div
            className="mx-auto w-full rounded-xl bg-white"
            style={{
              maxWidth: 900,
              maxHeight: '80vh',
              overflowY: 'auto',
              padding: 'var(--spacing-6)',
              boxShadow: 'var(--shadow-xl)',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: 'var(--spacing-4)',
              }}
            >
              <h3 style={{ margin: 0, fontSize: 'var(--font-size-base)', fontWeight: 'var(--font-weight-semibold)' }}>
                Filters
              </h3>
              <button
                type="button"
                onClick={() => setIsFilterModalOpen(false)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-gray-500)', padding: 4, fontSize: 'var(--font-size-xl)', lineHeight: 1 }}
                aria-label="Close filters"
              >
                ×
              </button>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <label className="flex flex-col gap-1">
                <span className="text-md font-semibold text-[var(--color-gray-700)]">File No</span>
                <input
                  type="text"
                  value={filters.fir_number ?? ''}
                  onChange={(e) => setFilter('fir_number', e.target.value)}
                  className="rounded-md border border-[var(--color-gray-300)] px-3 py-2 text-base bg-white"
                  placeholder="e.g. 0001 or 42/2026"
                />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-md font-semibold text-[var(--color-gray-700)]">Subject</span>
                <input
                  type="text"
                  value={filters.subject ?? ''}
                  onChange={(e) => setFilter('subject', e.target.value)}
                  className="rounded-md border border-[var(--color-gray-300)] px-3 py-2 text-base bg-white"
                  placeholder="Search by subject / description"
                />
              </label>
              <div className="flex flex-col gap-1 md:col-span-2">
                <span className="text-md font-semibold text-[var(--color-gray-700)]">Section / Sub-Section</span>
                <HierarchyNodePicker
                  key={filterResetKey}
                  permission="view"
                  onChange={(nodeId) => setFilter('node_id', nodeId ?? undefined)}
                />
              </div>
              {/* Hidden when no category master is defined in the Admin module. */}
              {categoryTemplate && (
                <label className="flex flex-col gap-1">
                  <span className="text-md font-semibold text-[var(--color-gray-700)]">{categoryLabel}</span>
                  <SearchableSelect
                    options={categoryOptions}
                    value={filters.category_node_id ? String(filters.category_node_id) : ''}
                    onChange={(val) => setFilter('category_node_id', val ? parseInt(val, 10) : undefined)}
                    placeholder={`All ${categoryLabel.toLowerCase()}`}
                    loading={lookupTemplatesQuery.isLoading || categoryNodesQuery.isLoading}
                    valueKey="id"
                  />
                </label>
              )}
              <label className="flex flex-col gap-1">
                <span className="text-md font-semibold text-[var(--color-gray-700)]">Classification</span>
                <SearchableSelect
                  options={CLASSIFICATION_OPTS}
                  value={filters.classification ?? ''}
                  onChange={(val) => setFilter('classification', val || undefined)}
                  placeholder="All classifications"
                />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-md font-semibold text-[var(--color-gray-700)]">Year</span>
                <SearchableSelect
                  options={yearOpts}
                  value={filters.year ? String(filters.year) : ''}
                  onChange={(val) => setFilter('year', val ? parseInt(val, 10) : undefined)}
                  placeholder="All years"
                />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-md font-semibold text-[var(--color-gray-700)]">Folder Type</span>
                <SearchableSelect
                  options={folderTypeOpts}
                  value={filters.folder_type ?? ''}
                  onChange={(val) => {
                    setFilter('folder_type', val || undefined);
                    setFilter('document_type', undefined);
                  }}
                  placeholder="All folder types"
                  loading={folderTypesQuery.isLoading}
                />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-md font-semibold text-[var(--color-gray-700)]">Document Type</span>
                <SearchableSelect
                  options={docTypeOpts}
                  value={filters.document_type ?? ''}
                  onChange={(val) => setFilter('document_type', val || undefined)}
                  placeholder={filters.folder_type ? 'All document types' : 'Select folder type first'}
                  disabled={!filters.folder_type}
                  loading={documentTypesQuery.isLoading}
                />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-md font-semibold text-[var(--color-gray-700)]">File Extension</span>
                <SearchableSelect options={extOpts} value={filters.file_extension ?? ''} onChange={(val) => setFilter('file_extension', val || undefined)} placeholder="All extensions" />
              </label>
              <label className="flex flex-col gap-1 md:col-span-2">
                <span className="text-md font-semibold text-[var(--color-gray-700)]">Hashtags (comma-separated)</span>
                <div className="relative">
                  <input
                    type="text"
                    value={filters.hashtags ?? ''}
                    onChange={async (e) => {
                      const value = e.target.value;
                      setFilter('hashtags', value);
                      const parts = value.split(',');
                      const last = parts[parts.length - 1].trim().replace(/^#/, '');
                      if (!last || last.length < 2) {
                        setHashtagSuggestions([]);
                        return;
                      }
                      try {
                        const res = await searchService.searchByHashtag([last], 1, 10);
                        const allTags = new Set<string>();
                        (res.data || []).forEach((row) => {
                          (row.hashtags || []).forEach((h) => {
                            if (typeof h === 'string' && h.toLowerCase().startsWith(last.toLowerCase())) allTags.add(h);
                          });
                        });
                        setHashtagSuggestions(Array.from(allTags));
                      } catch {
                        setHashtagSuggestions([]);
                      }
                    }}
                    className="w-full rounded-md border border-[var(--color-gray-300)] px-3 py-2 text-base bg-white"
                    placeholder="#murder, #2024"
                  />
                  {hashtagSuggestions.length > 0 && (
                    <div className="absolute z-10 mt-1 max-h-52 w-full overflow-y-auto rounded-md border border-[var(--color-gray-300)] bg-white shadow-md">
                      {hashtagSuggestions.map((h) => (
                        <button
                          key={h}
                          type="button"
                          onClick={() => {
                            const current = filters.hashtags ?? '';
                            const parts = current.split(',');
                            parts[parts.length - 1] = ` ${h}`.replace(/^ /, '#');
                            const next = parts.map((p) => p.trim()).filter(Boolean).join(', ');
                            setFilter('hashtags', next);
                            setHashtagSuggestions([]);
                          }}
                          className="flex w-full items-center justify-between px-3 py-1.5 text-md hover:bg-[var(--color-primary-50)]"
                        >
                          <span>#{h}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </label>
            </div>

            <div className="mt-6 flex justify-end gap-2">
              <Button variant="ghost" onClick={handleClearFilters}>
                Clear Filters
              </Button>
              <Button onClick={handleApplyFilters}>Apply Filters</Button>
            </div>
          </div>
        </div>
      )}

      {query.isLoading || query.isFetching ? (
        <div className="card p-4">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="h-12 rounded bg-gray-200 animate-pulse" style={{ width: '100%', marginBottom: i === 4 ? 0 : 'var(--spacing-3)' }} />
          ))}
        </div>
      ) : query.isError ? (
        <div
          className="card p-8 text-center"
          style={{
            padding: 'var(--spacing-8)',
            textAlign: 'center',
          }}
        >
          <p
            className="text-muted mb-4"
            style={{
              color: 'var(--color-gray-500)',
              marginBottom: 'var(--spacing-4)',
            }}
          >
            {(query.error as any)?.response?.data?.message ??
              'Failed to load upload status.'}
          </p>
          <button
            type="button"
            onClick={() => query.refetch()}
            className="rounded-md bg-primary px-4 py-2 text-md font-medium text-white hover:bg-primary-light"
          >
            Retry
          </button>
        </div>
      ) : items.length === 0 ? (
        <div
          className="card p-8 text-center"
          style={{
            padding: 'var(--spacing-8)',
            textAlign: 'center',
          }}
        >
          <FileText
            size={40}
            style={{
              color: 'var(--color-gray-300)',
              margin: '0 auto var(--spacing-4)',
              display: 'block',
            }}
          />
          <p
            className="text-muted"
            style={{ color: 'var(--color-gray-500)' }}
          >
            No {activeTab.toLowerCase()} documents
          </p>
        </div>
      ) : (
        <Table
          columns={columns}
          rows={items}
          rowKey={(row) => row.document_id}
          pagination={pagination}
          onPageChange={setPage}
          minWidth={activeTab === 'Denied' ? 1350 : 1150}
          renderExpandedRow={(row, _index, colSpan) => {
            const isExpanded = activeTab === 'Denied' && expandedId === row.document_id;
            if (!isExpanded) return null;

            return (
              <tr style={{ background: 'var(--color-gray-50)', borderBottom: '1px solid var(--color-gray-100)' }}>
                <td colSpan={colSpan} style={{ padding: '10px 16px', paddingLeft: 48, fontSize: 'var(--font-size-sm)', color: 'var(--color-gray-600)' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: 'var(--spacing-2)', alignItems: 'start' }}>
                    {row.denial_reason && (
                      <>
                        <span style={{ fontWeight: 600, color: 'var(--color-gray-700)' }}>Reason:</span>
                        <span>{row.denial_reason}</span>
                      </>
                    )}
                    {row.decision_at && (
                      <>
                        <span style={{ fontWeight: 600, color: 'var(--color-gray-700)' }}>Decision at:</span>
                        <span>{format(new Date(row.decision_at), 'dd MMM yyyy, HH:mm')}</span>
                      </>
                    )}
                    {(row.action_by ?? row.decided_by_user_id != null) && (
                      <>
                        <span style={{ fontWeight: 600, color: 'var(--color-gray-700)' }}>Action by:</span>
                        <span>{row.action_by ?? `User ID ${row.decided_by_user_id}`}</span>
                      </>
                    )}
                    {!row.denial_reason && !row.decision_at && !row.action_by && row.decided_by_user_id == null && (
                      <span style={{ gridColumn: 'span 2', color: 'var(--color-gray-500)' }}>
                        No denial details available.
                      </span>
                    )}
                  </div>
                </td>
              </tr>
            );
          }}
        />
      )}

      {deleteConfirm && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
        >
          <div
            style={{
              background: 'white',
              borderRadius: 'var(--radius-lg)',
              padding: 'var(--spacing-6)',
              width: '100%',
              maxWidth: '440px',
              boxShadow: 'var(--shadow-xl)',
            }}
          >
            <h3
              style={{
                margin: '0 0 var(--spacing-1)',
                fontSize: 'var(--font-size-base)',
                fontWeight: 'var(--font-weight-semibold)',
              }}
            >
              Remove rejected document?
            </h3>
            <p
              style={{
                margin: '0 0 var(--spacing-4)',
                fontSize: 'var(--font-size-sm)',
                color: 'var(--color-gray-600)',
              }}
            >
              This removes <strong>{deleteConfirm.fileName}</strong> from your upload list. The file stays on the
              server for audit purposes.
            </p>
            <div
              style={{
                display: 'flex',
                gap: 'var(--spacing-3)',
                justifyContent: 'flex-end',
              }}
            >
              <button
                type="button"
                onClick={() => setDeleteConfirm(null)}
                disabled={deleteMutation.isPending}
                style={{
                  padding: 'var(--spacing-2) var(--spacing-4)',
                  background: 'none',
                  border: '1px solid var(--color-gray-200)',
                  borderRadius: 'var(--radius-md)',
                  cursor: 'pointer',
                  fontSize: 'var(--font-size-sm)',
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  setActingId(deleteConfirm.documentId);
                  deleteMutation.mutate(deleteConfirm.documentId);
                }}
                disabled={deleteMutation.isPending}
                style={{
                  padding: 'var(--spacing-2) var(--spacing-4)',
                  background: 'var(--color-error)',
                  color: 'white',
                  border: 'none',
                  borderRadius: 'var(--radius-md)',
                  cursor: deleteMutation.isPending ? 'not-allowed' : 'pointer',
                  fontSize: 'var(--font-size-sm)',
                  fontWeight: 'var(--font-weight-semibold)',
                }}
              >
                {deleteMutation.isPending ? 'Removing…' : 'Remove'}
              </button>
            </div>
          </div>
        </div>
      )}

      {rejectModal && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
        }}>
          <div style={{
            background: 'white', borderRadius: 'var(--radius-lg)',
            padding: 'var(--spacing-6)', width: '100%', maxWidth: '440px',
            boxShadow: 'var(--shadow-xl)'
          }}>
            <h3 style={{ margin: '0 0 var(--spacing-1)', fontSize: 'var(--font-size-base)', fontWeight: 'var(--font-weight-semibold)' }}>
              Reject Document
            </h3>
            <p style={{ margin: '0 0 var(--spacing-4)', fontSize: 'var(--font-size-sm)', color: 'var(--color-gray-500)' }}>
              {rejectModal.fileName}
            </p>
            <label style={{ fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-medium)', display: 'block', marginBottom: 'var(--spacing-2)' }}>
              Reason for rejection <span className="text-error">*</span>
            </label>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Provide a clear reason for rejecting this document..."
              rows={3}
              style={{
                width: '100%', padding: 'var(--spacing-3)',
                border: `1px solid ${rejectReason.trim() ? 'var(--color-gray-200)' : 'var(--color-error)'}`,
                borderRadius: 'var(--radius-md)', fontSize: 'var(--font-size-sm)',
                resize: 'vertical', fontFamily: 'inherit', boxSizing: 'border-box'
              }}
            />
            {!rejectReason.trim() && (
              <p style={{ color: 'var(--color-error)', fontSize: 'var(--font-size-xs)', margin: 'var(--spacing-1) 0 0' }}>
                Reason is required
              </p>
            )}
            <div style={{ display: 'flex', gap: 'var(--spacing-3)', marginTop: 'var(--spacing-4)', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setRejectModal(null)}
                style={{
                  padding: 'var(--spacing-2) var(--spacing-4)', background: 'none',
                  border: '1px solid var(--color-gray-200)', borderRadius: 'var(--radius-md)',
                  cursor: 'pointer', fontSize: 'var(--font-size-sm)'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleRejectConfirm}
                disabled={!rejectReason.trim()}
                style={{
                  padding: 'var(--spacing-2) var(--spacing-4)',
                  background: rejectReason.trim() ? 'var(--color-error)' : 'var(--color-gray-100)',
                  color: rejectReason.trim() ? 'white' : 'var(--color-gray-400)',
                  border: 'none', borderRadius: 'var(--radius-md)',
                  cursor: rejectReason.trim() ? 'pointer' : 'not-allowed',
                  fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-semibold)'
                }}
              >
                Confirm Rejection
              </button>
            </div>
          </div>
        </div>
      )}

      {previewDocId && (
        <DocumentPreviewModal
          documentId={previewDocId}
          onClose={() => {
            setPreviewDocId(null);
            setPreviewContentKind(null);
          }}
          onRequestAccess={goToAccessRequest}
          onPreviewContentKind={setPreviewContentKind}
        />
      )}

      {accessDeniedDialogDocId && (
        <>
          <div
            role="presentation"
            onClick={() => setAccessDeniedDialogDocId(null)}
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(0,0,0,0.5)',
              zIndex: 9998,
            }}
          />
          <div
            role="dialog"
            aria-modal="true"
            style={{
              position: 'fixed',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 9999,
              background: 'white',
              borderRadius: 'var(--radius-lg)',
              border: '1px solid var(--color-error)',
              boxShadow: 'var(--shadow-xl)',
              width: '100%',
              maxWidth: 560,
              padding: 'var(--spacing-4)',
            }}
          >
            <p
              style={{
                fontSize: 'var(--font-size-sm)',
                color: 'var(--color-error)',
                marginBottom: 'var(--spacing-3)',
              }}
            >
              You do not have access to view or download this confidential
              document. Please send an access request to the Content Manager.
            </p>
            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 'var(--spacing-2)',
              }}
            >
              <button
                type="button"
                onClick={() => setAccessDeniedDialogDocId(null)}
                className="rounded-md border px-2 py-1 text-xs"
              >
                Close
              </button>
              <button
                type="button"
                onClick={() => {
                  const docId = accessDeniedDialogDocId;
                  setAccessDeniedDialogDocId(null);
                  goToAccessRequest(docId);
                }}
                className="rounded-md bg-[var(--color-primary)] px-2 py-1 text-xs font-medium text-white"
              >
                Send Request Access
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default UploadStatusPage;
